import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, MapPin, Phone, CheckCircle2, ArrowRight } from "lucide-react";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import ContactForm from "../../components/public/ContactForm";

function Markdown({ text, dark = false }) {
  // Very lightweight md → html: ##, ###, **bold**, - bullets, paragraphs
  if (!text) return null;
  const strongStyle = dark
    ? 'style="color:#fff;font-weight:800"'
    : 'style="color:#1E3A5F;font-weight:700"';
  const bodyCls = dark ? "text-white/90" : "text-[hsl(var(--foreground))]/85";
  const h2Cls = dark ? "text-white" : "text-[color:var(--brand-navy)]";
  const boldify = (s) => s.replace(/\*\*(.+?)\*\*/g, `<strong ${strongStyle}>$1</strong>`);
  const lines = text.split("\n");
  const out = [];
  let list = [];
  const flushList = () => {
    if (list.length) {
      out.push(
        <ul key={`ul-${out.length}`} className={`list-disc pl-6 space-y-1.5 ${bodyCls} my-4`}>
          {list.map((item, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: boldify(item) }} />
          ))}
        </ul>
      );
      list = [];
    }
  };
  lines.forEach((ln, i) => {
    const trimmed = ln.trim();
    if (trimmed.startsWith("### ")) { flushList(); out.push(<h3 key={i} className={`font-display text-xl md:text-2xl font-bold ${h2Cls} mt-8 mb-3`}>{trimmed.slice(4)}</h3>); }
    else if (trimmed.startsWith("## ")) { flushList(); out.push(<h2 key={i} className={`font-display text-2xl md:text-3xl font-black ${h2Cls} mt-10 mb-4`}>{trimmed.slice(3)}</h2>); }
    else if (/^[-*]\s+/.test(trimmed)) { list.push(trimmed.replace(/^[-*]\s+/, "")); }
    else if (trimmed === "") { flushList(); }
    else { flushList(); out.push(<p key={i} className={`${bodyCls} leading-relaxed mb-4`} dangerouslySetInnerHTML={{ __html: boldify(trimmed) }} />); }
  });
  flushList();
  return <>{out}</>;
}

export default function LocalPage() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const { settings: s } = useSettings();

  useEffect(() => {
    let alive = true;
    api.get(`/local-pages/${slug}`).then((r) => {
      if (!alive) return;
      setP(r.data);
      // Track local-page view for SEO analytics (ville × service)
      try {
        api.post("/track", {
          event: "local_page_view",
          path: `/intervention/${slug}`,
          referrer: document.referrer || "",
          meta: { slug, city: r.data?.city || "", service: r.data?.service_title || "" },
        });
      } catch (e) { /* ignore */ }
    }).catch(() => { if (alive) setP(false); });
    return () => { alive = false; };
  }, [slug]);

  if (p === false) {
    return (
      <div className="py-24 text-center" data-testid="local-page-404">
        <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Page introuvable</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">Cette intervention locale n'existe pas.</p>
        <Link to="/" className="inline-block mt-6 underline text-[color:var(--brand-sky)]">Retour à l'accueil</Link>
      </div>
    );
  }
  if (!p) return <div className="py-24 text-center text-sm text-[hsl(var(--muted-foreground))]">Chargement...</div>;

  const phone = s?.phone;

  return (
    <div data-testid="public-local-page">
      <title>{p.meta_title || p.title}</title>
      <meta name="description" content={p.meta_description || ""} />

      {/* Hero */}
      <section className="pt-12 pb-14 bg-[color:var(--brand-navy)] text-white noise-bg">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <nav className="text-xs text-white/60 mb-4 flex items-center gap-2 flex-wrap">
            <Link to="/" className="hover:text-white">Accueil</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/services" className="hover:text-white">Services</Link>
            <ChevronRight className="w-3 h-3" />
            <span>{p.service_title}</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[color:var(--brand-sky)]">{p.city}</span>
          </nav>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs font-bold uppercase tracking-wider mb-4">
            <MapPin className="w-3.5 h-3.5 text-[color:var(--brand-sky)]" /> {p.city} · {p.postal_code}
          </div>

          <h1 className="font-display text-3xl md:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight max-w-4xl">
            {p.title}
          </h1>

          {p.intro && (
            <div className="mt-6 text-lg md:text-xl text-white/85 leading-relaxed max-w-3xl">
              <Markdown text={p.intro} dark />
            </div>
          )}

          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/devis" className="inline-flex items-center gap-2 px-5 h-12 rounded-full bg-[color:var(--brand-red)] hover:bg-[color:var(--brand-red)]/90 text-white font-bold shadow-lg transition-all hover:-translate-y-0.5" data-testid="local-cta-devis">
              Devis gratuit sous 24h <ArrowRight className="w-4 h-4" />
            </Link>
            {phone && (
              <a href={`tel:${phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-2 px-5 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold transition-all" data-testid="local-cta-tel">
                <Phone className="w-4 h-4" /> {phone}
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-14">
        <div className="max-w-4xl mx-auto px-4 md:px-8 prose prose-lg max-w-none">
          <Markdown text={p.content} />
        </div>
      </section>

      {/* FAQ */}
      {p.faq?.length > 0 && (
        <section className="py-14 bg-[hsl(var(--muted))]/40">
          <div className="max-w-3xl mx-auto px-4 md:px-8">
            <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-bold mb-2">FAQ locale</div>
            <h2 className="font-display text-2xl md:text-4xl font-black text-[color:var(--brand-navy)] mb-8">
              Questions fréquentes à {p.city}
            </h2>
            <div className="space-y-3" data-testid="local-faq">
              {p.faq.map((f, i) => (
                <details key={i} className="group rounded-2xl bg-white border border-[hsl(var(--border))] p-5 shadow-soft open:shadow-md">
                  <summary className="cursor-pointer font-display font-bold text-[color:var(--brand-navy)] flex items-start gap-3 list-none">
                    <CheckCircle2 className="w-5 h-5 mt-0.5 text-[color:var(--brand-sky)] flex-shrink-0" />
                    <span className="flex-1">{f.question}</span>
                  </summary>
                  <div className="mt-3 pl-8 text-sm text-[hsl(var(--foreground))]/80 leading-relaxed">{f.answer}</div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact CTA */}
      <section className="py-14">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <ContactForm title={`Votre projet ${p.service_title.toLowerCase()} à ${p.city}`} description="Étude gratuite, devis détaillé sous 24h." />
        </div>
      </section>
    </div>
  );
}
